/**
 * Gate Entry vocabulary.
 *
 * A gate entry is the security desk's record of one truck, and its stages are NOT document
 * statuses: a gate entry has no lots, no lines, no ledger and no preview step, so it lives in its
 * own table rather than widening the four-state machine every arrival is checked against.
 *
 *     (an S-Capture dispatch) ──gate inward──▶ inward_done ──▶ outward_pending ──▶ outward_done
 *
 * Note what is NOT a stage: `inward_pending`. A truck waiting at the barrier has no row yet — it is
 * still only a dispatch in `pdn_inbox`. Materialising a row for it would mean the S-Capture pull
 * owned gate state, and a dispatch whose lots were never gated would leave a pending row nobody
 * clears. So "Inward Pending" is a TAB over the feed, not a stored value; `GATE_STAGES` is exactly
 * what a row can hold and exactly what the database enum declares.
 */
import { MOBILE_MESSAGE, MOBILE_PATTERN, normalizeMobile } from "./mobile";

export const GATE_TABS = [
  "all",
  "inward_pending",
  "inward_done",
  "outward_pending",
  "outward_done",
] as const;
export type GateTab = (typeof GATE_TABS)[number];

/** What a `gate_entries` row can BE. The `gate_stage` pgEnum is this list, in this order. */
export const GATE_STAGES = [
  "inward_done",
  "outward_pending",
  "outward_done",
] as const;
export type GateStage = (typeof GATE_STAGES)[number];

/**
 * Tabs this phase actually maintains. All five do since outward passes are recorded: Outward
 * Pending is every submitted Stock Transfer still at its sending plant, Outward Done its passes out.
 */
export const GATE_TABS_IMPLEMENTED: ReadonlySet<GateTab> = new Set<GateTab>(
  GATE_TABS
);

/** Tabs that carry a count pill — the implemented ones, in tab order. */
export const GATE_COUNT_TABS = GATE_TABS.filter((t) =>
  GATE_TABS_IMPLEMENTED.has(t)
);

export type GateCounts = Record<(typeof GATE_COUNT_TABS)[number], number>;

// ------------------------------------------------------------------ Truck details

/** The details a guard reports for a truck whose transport changed, in the order the screen shows them. */
export type TruckDetailField =
  | "vehicleNumber"
  | "transportName"
  | "driverName"
  | "driverNo"
  | "lorryReceiptNumber";

// \p{M} keeps Devanagari names whole: their vowel signs are marks, not letters.
const PERSON_OR_FIRM = /^[\p{L}\p{M} .&]+$/u;
const PERSON_OR_FIRM_MESSAGE = "Use letters, spaces, . and & only";

const TRUCK_FIELD_RULES: Record<
  TruckDetailField,
  {
    max: number;
    pattern?: RegExp;
    message?: string;
    normalize?: (value: string) => string;
  }
> = {
  // Its format is judged by the API (`assertVehicleNumber`), not here.
  vehicleNumber: { max: 30 },
  transportName: {
    max: 150,
    pattern: PERSON_OR_FIRM,
    message: PERSON_OR_FIRM_MESSAGE,
  },
  driverName: {
    max: 150,
    pattern: PERSON_OR_FIRM,
    message: PERSON_OR_FIRM_MESSAGE,
  },
  driverNo: {
    max: 20,
    pattern: MOBILE_PATTERN,
    message: MOBILE_MESSAGE,
    normalize: normalizeMobile,
  },
  lorryReceiptNumber: {
    max: 100,
    pattern: /^[A-Za-z0-9/-]+$/,
    message: "Use letters, digits, / and - only",
  },
};

/** A detail as it is judged and stored: trimmed, and a mobile reduced to its ten digits. */
export function normalizeTruckField(
  key: TruckDetailField,
  value: string | null | undefined
): string {
  const trimmed = (value ?? "").trim();
  return TRUCK_FIELD_RULES[key].normalize?.(trimmed) ?? trimmed;
}

/**
 * Why a truck detail is refused, or undefined when it is fine. A blank detail is fine; the web and
 * the API both ask this, so a value the sheet accepts is never refused on submit.
 */
export function truckFieldError(
  key: TruckDetailField,
  value: string | null | undefined
): string | undefined {
  const rule = TRUCK_FIELD_RULES[key];
  const v = normalizeTruckField(key, value);
  if (!v) return undefined;
  if (v.length > rule.max) return `Keep it to ${rule.max} characters`;
  if (rule.pattern && !rule.pattern.test(v)) return rule.message;
  return undefined;
}
